import GLTFLoader from 'three-gltf-loader';
import {spine} from '../libs/spine-threejs';
import ThreeSpine from './ThreeSpine';
import {KTXLoader} from 'three/examples/jsm/loaders/KTXLoader';
import common from './common';
export default class assets {
	constructor() {
		this.signals = {};
		this.signals.complete = new P3.utils.MiniSignal();

		this._modelFiles = [];
		this._animFiles = [];
		this._textureFiles = [];
		this._compressedFiles = [];
		this._spineFiles = [];
		this._loadCounter = 0;
		this._spineLoadCounter = 0;
		this._spineCreator = null;
		this._requiredTextures = [];
		this._spinesLoaded = false;
		this._assetsLoaded = false;
		this._loadingCompressed = false;

		this._spineAssets = new spine.threejs.AssetManager('./assets/spine');
		this._assets = new Map();
	}

	addFiles(files) {
		for (const item of files) {
			if (item.type === '3d') {
				this._modelFiles.push(item);
			} else if (item.type === '3danim') {
				this._animFiles.push(item);
			} else if (item.type === 'texture') {
				this._textureFiles.push(item);
			} else if (item.type === 'spine') {
				this._spineFiles.push(item);
			} else if (item.type === 'ctexture') {
				this._compressedFiles.push(item);
			}
		}
	}

	loadFiles() {
		THREE.DefaultLoadingManager.onLoad = () => {
			console.log('Loaded meshes');
			this._assetsLoaded = true;
		};

		const gltfloader = new GLTFLoader();

		for (let i = 0; i < this._modelFiles.length; i++) {
			gltfloader.load(
				this._modelFiles[i].path,
				(object) => {
					if (object.scene) {
						object.scene.traverse(item => {
							if (item.isMesh && item.material.map) {
								const url = item.material.map.image.currentSrc;
								let name = url.substring(url.lastIndexOf('/') + 1).replace(/\.[^/.]+$/, '');
								this._requiredTextures.push({name: name, object: item.material});
							}
						});
					}

					this._assets.set(this._modelFiles[i].name.toLowerCase(), object.scene);
				}, undefined,
				(error) => {
					console.error(`ERROR LOADING GLTF: ${this._modelFiles[i].path}`, error);
					return false;
				}
			);
		}

		for (let i = 0; i < this._animFiles.length; i++) {
			gltfloader.load(
				this._animFiles[i].path,
				(object) => {
					const toremove = [];

					object.scene.traverse((node) => {
						if (node.name === 'Lamp' || node.name === 'Camera') {
							toremove.push(node);
						}
					});

					for (const item of toremove) {
						object.scene.remove(item);
					}

					this._assets.set(this._animFiles[i].name.toLowerCase(), object);
				}, undefined,
				(error) => {
					console.error(`ERROR LOADING ANIM: ${this._animFiles[i].path}`, error);

					return false;
				}
			);
		}

		const textureLoader = new THREE.TextureLoader();
		for (let i = 0; i < this._textureFiles.length; i++) {
			textureLoader.load(
				this._textureFiles[i].path,
				(object) => {
					this._assets.set(this._textureFiles[i].name.toLowerCase(), object);
				}, undefined,
				(error) => {
					console.error(`ERROR LOADING TEXTURE: ${this._textureFiles[i].path}`, error);
					return false;
				}
			);
		}

		const ktxLoader = new KTXLoader();
		for (let i = 0; i < this._compressedFiles.length; i++) {
			ktxLoader.load(
				this._compressedFiles[i].path,
				(object) => {
					this._assets.set(this._compressedFiles[i].name.toLowerCase(), object);
				}
			);
		}

		for (let i = 0; i < this._spineFiles.length; i++) {
			this._spineAssets.loadText(`/${this._spineFiles[i].name}.json`);
			this._spineAssets.loadTextureAtlas(`/${this._spineFiles[i].name}.atlas`, () => this.onSpineLoad());
		}
	}

	_loadCompressedTextures() {
		this._loadingCompressed = true;

		const textureLoadingManager = new THREE.LoadingManager();
		textureLoadingManager.onStart = () => {
			console.log('[ASSETS] Loading compressed textures');
		};
		textureLoadingManager.onLoad = () => {
			console.log('Loaded compressed textures');
			this.signals.complete.dispatch(this);
		};

		this._requiredTextures = [...new Set(this._requiredTextures)];
		let folder = '';
		if (common.formats.pvrtc) {
			folder = 'pvrtc1_2';
		} else if (common.formats.etc1) {
			folder = 'etc1';
		} else if (common.formats.s3tc) {
			folder = 'dxt1';
		}

		/* if (common.lowPerformance) {
			let tmap = P3.assets.json('TextureMapping');
			this._requiredTextures.map(e => {
				if (e.name in tmap) {
					e.name = tmap[e.name];
				} else {
					console.warn(`found no replacement for ${e.name}`);
				}
			});
		} */

		const sRGBTextures = [
			'lab_terminal'
		];

		console.log(`Loading ${folder} textures`);
		let ktxLoader = new KTXLoader(textureLoadingManager);
		for (let i = 0; i < this._requiredTextures.length; i++) {
			ktxLoader.load(
				`./assets/3d/compressed-textures/${folder}/${this._requiredTextures[i].name}.ktx`,
				object => {
					if (sRGBTextures.includes(this._requiredTextures[i].name)) {
						object.encoding = THREE.sRGBEncoding;
					}

					this._requiredTextures[i].object.map.dispose();
					this._requiredTextures[i].object.map = object;
				}
			);
		}
	}

	model(file) {
		const asset = this._assets.get(file.toLowerCase()).clone().children[0];

		if (asset === undefined) {
			console.error(`[3JS] Asset not found: ${file}\nUsing fallback mesh`);

			const geo = new THREE.BoxGeometry(1, 1, 1);
			const mat = new THREE.MeshBasicMaterial({color: 0xff00dc});

			return new THREE.Mesh(geo, mat);
		}

		return asset;
	}

	anim(file) {
		const asset = this._assets.get(file.toLowerCase());

		if (asset === undefined) {
			// eslint-disable-next-line no-console
			console.error(`[3JS] Asset not found: ${file}\nUsing fallback mesh`);

			const geo = new THREE.BoxGeometry(1, 1, 1);
			const mat = new THREE.MeshBasicMaterial({color: 0xff00dc});

			return new THREE.Mesh(geo, mat);
		}

		return this.cloneGLTF(asset);
	}

	texture(file) {
		const asset = this._assets.get(file.toLowerCase());

		if (asset === undefined) {
			// eslint-disable-next-line no-console
			console.error(`[3JS] Asset not found: ${file}\nUsing fallback texture`);

			const tex = new THREE.TextureLoader().load('./assets/images/checkerboard.png');

			tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
			tex.magFilter = tex.minFilter = THREE.NearestFilter;

			return tex;
		}

		return asset;
	}

	spine(file) {
		return this._spineCreator.get(file);
	}

	onSpineLoad() {
		this._spineLoadCounter++;
		console.log(`Loaded spine! (${this._spineLoadCounter}/${this._spineFiles.length})`);
		if (this._spineLoadCounter === this._spineFiles.length) {
			let names = [];
			for (const spine of this._spineFiles) {
				names.push(spine.name);
			}
			this._spineCreator = new ThreeSpine(names);
            this._spinesLoaded = true;
		}
	}

	update() {
		if (!this._loadingCompressed && this._spinesLoaded && this._assetsLoaded) {
			this._loadCompressedTextures();
		}
	}

	onSpineError(e) {
		throw new Error(`Failed loading spine: ${e}`);
	}

	// from: https://gist.github.com/cdata/f2d7a6ccdec071839bc1954c32595e87
	cloneGLTF(gltf) {
		const clone = {
			animations : gltf.animations,
			scene      : gltf.scene.clone(true)
		};

		const skinnedMeshes = {};

		gltf.scene.traverse((node) => {
			if (node.isSkinnedMesh) {
				skinnedMeshes[node.name] = node;
			}
		});
		const cloneBones = {};
		const cloneSkinnedMeshes = {};

		clone.scene.traverse((node) => {
			if (node.isBone) {
				cloneBones[node.name] = node;
			}

			if (node.isSkinnedMesh) {
				cloneSkinnedMeshes[node.name] = node;
			}
		});

		for (const name in skinnedMeshes) {
			const skinnedMesh = skinnedMeshes[name];
			const skeleton = skinnedMesh.skeleton;
			const cloneskinnedMesh = cloneSkinnedMeshes[name];

			const orderedCloneBones = [];

			for (let i = 0; i < skeleton.bones.length; i++) {
				const cloneBone = cloneBones[skeleton.bones[i].name];

				orderedCloneBones.push(cloneBone);
			}

			cloneskinnedMesh.bind(new THREE.Skeleton(orderedCloneBones, skeleton.boneInverses), cloneskinnedMesh.matrixWorld);
		}

		return clone;
	}

	get assets() {
		return this._assets;
	}

	get spineCache() {
		return this._spineAssets;
	}
}
